import '../css/app.css';

import { createApp, h } from 'vue';
import { createInertiaApp } from '@inertiajs/vue3';
import { ZiggyVue } from 'ziggy-js';
import Vue3Toastify, { type ToastContainerOptions } from 'vue3-toastify';
import * as Sentry from '@sentry/vue';
import 'vue3-toastify/dist/index.css';
import posthogPlugin from './plugins/posthog';
import Vapor from 'laravel-vapor';
import dayjs from 'dayjs';
import 'dayjs/locale/nb';
import relativeTime from 'dayjs/plugin/relativeTime';
import localizedFormat from 'dayjs/plugin/localizedFormat'; // ES 2015
dayjs.locale('nb');
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime); // Extend Day.js with the plugin
Vapor.withBaseAssetUrl(import.meta.env.VITE_VAPOR_ASSET_URL);

const appName = import.meta.env.VITE_APP_NAME || 'Laravel';

createInertiaApp({
	title: (title) => `${title} - ${appName}`,
	resolve: (name) => {
		const pages = import.meta.glob('./Pages/**/*.vue');
		return pages[`./Pages/${name}.vue`]();
	},
	setup({ el, App, props, plugin }) {
		const app = createApp({ render: () => h(App, props) });
		app.provide('$dayjs', dayjs);
		Sentry.init({
			app,
			dsn: import.meta.env.VITE_SENTRY_DSN,
			environment: import.meta.env.VITE_APP_ENV,
			debug: import.meta.env.VITE_SENTRY_DEBUG ? true : false,
			integrations: [
				Sentry.replayIntegration({
					// NOTE: This will disable built-in masking.
					// Only use this if your site has no sensitive data.
					maskAllText: false,
					blockAllMedia: false,
				}),
			],
			replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
			replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
		});

		const user = props.initialPage.props.auth?.user;
		if (user) {
			Sentry.setUser({ id: user.id, email: user.email, team: user?.current_team_id ?? null });
		}

		app.use(plugin)
			.use(ZiggyVue)
			.use(Vue3Toastify, {
				autoClose: 3000,
			} as ToastContainerOptions)
			.use(posthogPlugin)
			.mount(el);

		return app;
	},
	progress: {
		color: '#4B5563',
	},
});
